/**
 * Shared artifact download trigger.
 *
 * The backend returns presigned storage URLs joined into a single comma-separated
 * string (see GetResultsUseCase), so callers hand the raw string in and this module
 * owns splitting it.
 *
 * Two deliberate choices, both of which previously caused multi-file downloads to fail:
 *
 * 1. No stagger. Downloads must be started in one synchronous burst. Spacing them with
 *    setTimeout pushes every file after the first outside the browser's transient
 *    user-activation window, at which point Chromium blocks them silently and only the
 *    first file arrives.
 * 2. Hidden iframes, not anchors. Presigned URLs are cross-origin, so the anchor
 *    `download` attribute is ignored and any storage object served without
 *    `Content-Disposition: attachment` (csv/tsv/txt/json results) would navigate the
 *    app away instead of downloading. An iframe confines that to an invisible frame.
 */

// Only guards against tearing the frame down before response headers arrive; once the
// browser has handed the response to its download manager the transfer is independent
// of the frame, so this does not cap how long a large file may take to transfer.
const FRAME_CLEANUP_DELAY_MS = 120_000;

export const parseDownloadUrls = (downloadUrl: string): string[] =>
  downloadUrl
    .split(",")
    .map((url) => url.trim())
    .filter((url) => url.length > 0);

const triggerDownload = (url: string) => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.append(iframe);

  setTimeout(() => {
    if (iframe.parentNode) {
      iframe.remove();
    }
  }, FRAME_CLEANUP_DELAY_MS);
};

/**
 * Starts a download for every URL in the comma-separated payload.
 * Returns the number of downloads started so callers can distinguish an empty
 * backend response from a successful one.
 */
export const triggerArtifactDownloads = (downloadUrl: string): number => {
  const urls = parseDownloadUrls(downloadUrl);
  for (const url of urls) {
    triggerDownload(url);
  }
  return urls.length;
};
